import { Field, useFormikContext } from 'formik'
import React from 'react'
import { Link } from 'react-router-dom'

import { Checkbox, Fieldset, Select, Subsection } from '@fourthwall/components'
import { routing } from '@utils/routing'

import type { FieldValues, FormValues } from '../../EmailNotifications.types'
import * as S from './AccountNotifications.styled'

export const AccountNotifications = () => {
  const { values, setFieldValue } = useFormikContext<FieldValues>()

  return (
    <Subsection title="Account notifications">
      <div style={{ marginBottom: 16 }}>
        <p>
          You may still receive occasional personalized emails from our team based on your account
          status.
        </p>
      </div>
      <Fieldset>
        <Fieldset.Row>
          <Fieldset.Item>
            <Checkbox
              value={values.newOrder !== 'NEVER'}
              onValueChange={(value) => {
                if (value === false) {
                  setFieldValue(
                    'newOrder' satisfies FormValues,
                    'NEVER' satisfies FieldValues['newOrder'],
                  )
                } else {
                  setFieldValue(
                    'newOrder' satisfies FormValues,
                    'DAILY' satisfies FieldValues['newOrder'],
                  )
                }
              }}
              label="New orders received"
              description="Notify me about new orders or donations"
            />
            {values.newOrder !== 'NEVER' && (
              <Fieldset.Row inner>
                <S.SelectWrapper>
                  <Fieldset.Item>
                    <Field
                      component={Select}
                      name={'newOrder' satisfies FormValues}
                      options={[
                        { value: 'DAILY' satisfies FieldValues['newOrder'], label: 'Daily' },
                        { value: 'INSTANT' satisfies FieldValues['newOrder'], label: 'Instant' },
                      ]}
                      label="Notification frequency"
                    />
                  </Fieldset.Item>
                </S.SelectWrapper>
              </Fieldset.Row>
            )}
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name={'offerVariantStatusUpdates' satisfies FormValues}
              label="Fourthwall-manufactured products going out of stock"
              description="Notify me when a Fourthwall-manufactured product goes out of stock"
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name={'paymentMethodCharges' satisfies FormValues}
              label="Payment method charges"
              description={
                <>
                  Notify me when my{' '}
                  <Link to={routing.settings.billing.self} target="_blank">
                    payment method
                  </Link>{' '}
                  is charged
                </>
              }
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name={'monthlyChargeLimitReached' satisfies FormValues}
              label="Monthly charge limit reached"
              description={
                <>
                  Notify me when my{' '}
                  <Link to={routing.settings.billing.self} target="_blank">
                    monthly charge limit
                  </Link>{' '}
                  is reached
                </>
              }
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name={'blockedTransactions' satisfies FormValues}
              label="Blocked transactions"
              description={
                <>
                  Notify me when a{' '}
                  <Link to={routing.blockedTransactions} target="_blank">
                    transaction is blocked
                  </Link>
                </>
              }
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Checkbox}
              name={'twitchGiftingSummaries' satisfies FormValues}
              label="Twitch gifting summaries"
              description={
                <>
                  Send me a summary of recent{' '}
                  <Link to={routing.contributions.orders.giveaways.all} target="_blank">
                    Twitch gifts
                  </Link>{' '}
                  (including a clip of the giveaway)
                </>
              }
            />
          </Fieldset.Item>
        </Fieldset.Row>
      </Fieldset>
    </Subsection>
  )
}
